/**
 * 래그돌 포저 — 관절에 핀을 찍고 뼈를 선으로 잇고, 핀을 잡아 끌어 자세를 잡는다.
 *
 * Chris 2026-09-08: 「각 관절에 핀포인트, 관절마다 이어지는 선, 개리모드 래그돌처럼」.
 * 물리는 없다 — 핀을 끌면 **그 관절의 부모 뼈**가 끌린 지점을 향하도록 돈다(손을 끌면 아래팔이,
 * 아래팔(팔꿈치)을 끌면 위팔이). 돌아간 결과는 관절 한계 표(`motion.JOINTS`)의 파라미터로
 * 분해해 한계로 자른 뒤 `applyPose` 로 다시 세운다 — 그래서 어디로 끌어도 무릎이 옆으로 안 꺾인다.
 * 자세는 포즈 JSON 이 진실이고, 시신 파이프라인(눕히기·굴리기·바닥 맞추기)은 그 위에서 그대로 돈다.
 */
import * as THREE from 'three';
import { JOINTS, jointLimits, setJointLimits, type Pose, type RestPose } from './motion';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';

export interface PoserTarget {
  model: () => THREE.Object3D | null;
  rest: () => RestPose | null;
  getPose: () => Pose;
  setPose: (p: Pose) => void;
}

/** 핀을 찍는 관절과, 그 핀을 끌 때 도는 부모 관절 */
const PINS: Array<[string, string | null]> = [
  ['Head', 'Neck'], ['Neck', 'Spine2'], ['Spine2', 'Spine1'], ['Spine1', 'Spine'], ['Spine', null], ['Hips', null],
  ['LeftShoulder', null], ['LeftArm', null], ['LeftForeArm', 'LeftArm'], ['LeftHand', 'LeftForeArm'],
  ['RightShoulder', null], ['RightArm', null], ['RightForeArm', 'RightArm'], ['RightHand', 'RightForeArm'],
  ['LeftUpLeg', null], ['LeftLeg', 'LeftUpLeg'], ['LeftFoot', 'LeftLeg'], ['LeftToeBase', 'LeftFoot'],
  ['RightUpLeg', null], ['RightLeg', 'RightUpLeg'], ['RightFoot', 'RightLeg'], ['RightToeBase', 'RightFoot'],
];

const boneOf = (root: THREE.Object3D, suffix: string): THREE.Bone | null => {
  const found: THREE.Bone[] = [];
  root.traverse((o) => { if (!found.length && (o as THREE.Bone).isBone && o.name.endsWith(suffix)) found.push(o as THREE.Bone); });
  return found[0] ?? null;
};

/** 뼈의 현재 회전을 바인드 기준 상대 회전으로 풀어 관절 파라미터(한계 안)로 만든다 */
function decompose(joint: string, rel: THREE.Quaternion): Record<string, number> {
  const spec = JOINTS[joint];
  const out: Record<string, number> = {};
  if (!spec) return out;
  // applyPose 는 X → Z → Y 순서로 곱한다(굽힘·벌림·비틀림) → 같은 순서의 오일러로 푼다
  const e = new THREE.Euler().setFromQuaternion(rel, 'XZY');
  const byAxis: Record<'X' | 'Y' | 'Z', number> = { X: e.x, Y: e.y, Z: e.z };
  for (const [p, q] of Object.entries(spec)) {
    const v = byAxis[q.axis] * q.sign;
    out[p] = jointLimits ? Math.min(q.max, Math.max(q.min, v)) : v;
  }
  return out;
}

export function createRagdollPoser(scene: THREE.Scene, camera: THREE.Camera, canvas: HTMLElement, target: PoserTarget, opts: { panel?: boolean } = {}) {
  const group = new THREE.Group();
  group.name = 'ragdoll-poser';
  let helper: THREE.SkeletonHelper | null = null;
  const pins = new Map<string, THREE.Mesh>();
  const pinMat = new THREE.MeshBasicMaterial({ color: '#ffd24a', depthTest: false });
  const grabMat = new THREE.MeshBasicMaterial({ color: '#ff5a3c', depthTest: false });
  const fixMat = new THREE.MeshBasicMaterial({ color: '#8fa3b8', depthTest: false });
  const ray = new THREE.Raycaster();
  const plane = new THREE.Plane();
  const tmp = new THREE.Vector3();
  let grabbed: string | null = null;
  let hovered: string | null = null;   // 마우스 아래 핀 — R/T/Y 가 이걸 잡는다
  let on = false;
  let downAt: [number, number] | null = null;
  let moved = false;
  /**
   * **기즈모(TransformControls).** 핀을 클릭(끌지 않고)하면 그 관절에 붙는다.
   * 1 회전 · 2 이동 · 3 길이. 회전은 관절 한계로 잘리고, 이동은 골반이면 몸 전체·나머지는 뼈 오프셋,
   * 길이는 뼈 스케일이다. 값은 전부 포즈 JSON 으로 들어간다 (Chris 09-08: 「게리처럼 편하게」)
   */
  const tc = new TransformControls(camera, canvas);
  tc.setSize(0.7);
  tc.setSpace('local');
  let attached: string | null = null;
  let baseWorld = new THREE.Vector3();
  const onObjectChange = () => {
    if (!attached) return;
    const model = target.model(); const rest = target.rest();
    if (!model || !rest) return;
    const b = boneOf(model, attached); if (!b) return;
    const r = rest.get(b.name); if (!r) return;
    const pose: Pose = JSON.parse(JSON.stringify(target.getPose()));
    if (tc.mode === 'rotate') {
      const rel = r.q.clone().invert().multiply(b.quaternion);
      if (attached === 'Hips') {
        const e = new THREE.Euler().setFromQuaternion(rel, 'YXZ');
        pose.__root = { ...(pose.__root ?? {}), yaw: e.y, roll: e.x };
      } else if (JOINTS[attached]) {
        if (jointLimits) {
          pose[attached] = decompose(attached, rel);
          if (pose.__quat) delete pose.__quat[attached];
        } else {
          // **한계 끔 = 원 회전 그대로.** 오일러 분해는 두 번째 축 90도 근처에서 부호가 뒤집혀
          // 「몸통을 돌리면 몸 전체가 갑자기 꼬이는」 그림이 됐다(Chris 09-08). 사원수로 저장한다
          pose.__quat = { ...(pose.__quat ?? {}), [attached]: rel.toArray() as number[] };
          pose[attached] = decompose(attached, rel);   // 슬라이더 표시용 근사치
        }
      } else return;
    } else if (tc.mode === 'translate') {
      if (attached === 'Hips') {
        const now = b.getWorldPosition(new THREE.Vector3());
        const d = now.sub(baseWorld);
        const gq = group.parent ? group.parent.getWorldQuaternion(new THREE.Quaternion()).invert() : new THREE.Quaternion();
        d.applyQuaternion(gq);
        const cur = pose.__root ?? {};
        pose.__root = { ...cur, x: (cur.x ?? 0) + d.x, y: (cur.y ?? 0) + d.y, z: (cur.z ?? 0) + d.z };
      } else {
        const off = b.position.clone().sub(r.p);
        pose.__offset = { ...(pose.__offset ?? {}), [attached]: off.toArray() };
      }
    } else if (tc.mode === 'scale') {
      pose.__scale = { ...(pose.__scale ?? {}), [attached]: b.scale.toArray() };
    }
    target.setPose(pose);
    const bb = boneOf(model, attached); if (bb) baseWorld = bb.getWorldPosition(new THREE.Vector3());
    update();
  };
  tc.addEventListener('objectChange', onObjectChange);
  const attach = (joint: string) => {
    const model = target.model(); if (!model) return false;
    const b = boneOf(model, joint); if (!b) return false;
    attached = joint;
    baseWorld = b.getWorldPosition(new THREE.Vector3());
    tc.attach(b);
    for (const [n, m] of pins) (m as THREE.Mesh).material = n === joint ? grabMat : (m.userData.parent ? pinMat : fixMat);
    return true;
  };
  const detach = () => { attached = null; tc.detach(); for (const [, m] of pins) (m as THREE.Mesh).material = m.userData.parent ? pinMat : fixMat; };
  const onKey = (e: KeyboardEvent) => {
    if (!on) return;
    // **핀을 짚고 R** — 잡고 있거나 마우스 아래에 있는 핀에 회전 기즈모가 붙는다. T 이동, Y 길이.
    // (S 는 게임 이동키라 안 쓴다.) 숫자 1·2·3 은 붙어 있는 기즈모의 모드만 바꾼다
    const pick = grabbed ?? hovered ?? attached;
    const modeKey: Record<string, 'rotate' | 'translate' | 'scale'> = { KeyR: 'rotate', KeyT: 'translate', KeyY: 'scale', Digit1: 'rotate', Digit2: 'translate', Digit3: 'scale' };
    const m = modeKey[e.code];
    if (m) {
      if (pick && pick !== attached) attach(pick);
      tc.setMode(m);
      if (grabbed) { pins.get(grabbed)!.material = grabMat; grabbed = null; downAt = null; }   // 끌기 중이면 끌기를 끝내고 기즈모로
    } else if (e.code === 'Escape') detach();
    else return;
    e.stopImmediatePropagation();
    e.preventDefault();
  };

  const build = () => {
    const model = target.model();
    if (!model) return false;
    helper = new THREE.SkeletonHelper(model);
    (helper.material as THREE.LineBasicMaterial).depthTest = false;
    (helper.material as THREE.LineBasicMaterial).linewidth = 2;
    group.add(helper);
    for (const [name, parent] of PINS) {
      const b = boneOf(model, name);
      if (!b) continue;
      const m = new THREE.Mesh(new THREE.SphereGeometry(parent ? 0.035 : 0.025, 12, 10), parent ? pinMat : fixMat);
      m.renderOrder = 999;
      m.userData.joint = name;
      m.userData.parent = parent;
      pins.set(name, m);
      group.add(m);
    }
    scene.add(group);
    return true;
  };

  const update = () => {
    if (!on) return;
    const model = target.model();
    if (!model) return;
    for (const [name, m] of pins) {
      const b = boneOf(model, name);
      if (b) b.getWorldPosition(m.position);
    }
  };

  const ndc = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    return new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  };

  const onDown = (e: PointerEvent) => {
    if (!on || e.button !== 0) return;
    if (tc.axis) return;   // 기즈모 손잡이 위 — TransformControls 가 가져간다
    ray.setFromCamera(ndc(e), camera);
    const hits = ray.intersectObjects([...pins.values()], false);
    const hit = hits[0];
    if (!hit) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    downAt = [e.clientX, e.clientY]; moved = false;
    if ((hit.object.userData.parent as string | null) === null) { attach(hit.object.userData.joint as string); return; }
    grabbed = hit.object.userData.joint as string;
    (hit.object as THREE.Mesh).material = grabMat;
    // 끄는 평면: 카메라를 마주 보고 핀을 지나는 평면
    camera.getWorldDirection(tmp);
    plane.setFromNormalAndCoplanarPoint(tmp, hit.object.position);
  };

  const onMove = (e: PointerEvent) => {
    if (!on) return;
    if (!grabbed) {
      // 핀 위에 마우스가 있으면 기억해 둔다 — 「핀 짚고 R」(Chris 09-08)
      ray.setFromCamera(ndc(e), camera);
      const h = ray.intersectObjects([...pins.values()], false)[0];
      hovered = h ? (h.object.userData.joint as string) : null;
      return;
    }
    if (downAt && Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]) < 4) return;
    moved = true;
    e.stopImmediatePropagation();
    const model = target.model();
    const rest = target.rest();
    if (!model || !rest) return;
    const pin = pins.get(grabbed)!;
    const parentName = pin.userData.parent as string;
    const joint = boneOf(model, grabbed);
    const parent = boneOf(model, parentName);
    if (!joint || !parent || !parent.parent) return;
    ray.setFromCamera(ndc(e), camera);
    const hitP = new THREE.Vector3();
    if (!ray.ray.intersectPlane(plane, hitP)) return;
    // 부모 뼈를 돌려서 (부모→관절) 방향이 (부모→끌린 점) 이 되게 한다
    const pw = parent.getWorldPosition(new THREE.Vector3());
    const cur = joint.getWorldPosition(new THREE.Vector3()).sub(pw).normalize();
    const want = hitP.clone().sub(pw).normalize();
    if (cur.lengthSq() < 1e-8 || want.lengthSq() < 1e-8) return;
    const R = new THREE.Quaternion().setFromUnitVectors(cur, want);
    const P = parent.parent.getWorldQuaternion(new THREE.Quaternion());
    const local = P.clone().invert().multiply(R).multiply(P);
    const next = local.multiply(parent.quaternion);
    // 바인드 기준 상대 회전 → 관절 파라미터(한계) → 포즈 JSON 갱신 → 전체 다시 세움
    const restB = rest.get(parent.name);
    if (!restB) return;
    const rel = restB.q.clone().invert().multiply(next);
    const pose = { ...target.getPose(), [parentName]: decompose(parentName, rel) };
    target.setPose(pose);
    update();
  };

  const onUp = (e: PointerEvent) => {
    if (!on || !grabbed) return;
    e.stopImmediatePropagation();
    const j = grabbed;
    pins.get(j)!.material = pinMat;
    grabbed = null; downAt = null;
    if (!moved) attach(j);   // 끌지 않고 클릭 → 기즈모
  };

  /** 화면 패널 — 콘솔 없이 모드 바꾸고 JSON 을 복사한다 (Chris 09-08: 「내가 직접 수정도 가능하냐」) */
  let panel: HTMLDivElement | null = null;
  const makePanel = () => {
    panel = document.createElement('div');
    panel.style.cssText = 'position:fixed;left:50%;bottom:14px;transform:translateX(-50%);display:flex;gap:6px;align-items:center;background:rgba(10,12,16,.92);color:#ddd;font:12px sans-serif;padding:8px 10px;border:1px solid #444;border-radius:8px;z-index:99';
    const btn = (label: string, fn: () => void) => { const b = document.createElement('button'); b.textContent = label; b.style.cssText = 'font:12px sans-serif;padding:4px 10px;background:#2a2f38;color:#eee;border:1px solid #555;border-radius:5px;cursor:pointer'; b.onclick = (e) => { e.stopPropagation(); fn(); }; panel!.appendChild(b); return b; };
    btn('1 회전', () => tc.setMode('rotate')); btn('2 이동', () => tc.setMode('translate')); btn('3 길이', () => tc.setMode('scale'));
    btn('떼기 Esc', () => detach());
    const lim = btn(`한계 ${jointLimits ? '켬' : '끔'}`, () => { setJointLimits(!jointLimits); lim.textContent = `한계 ${jointLimits ? '켬' : '끔'}`; lim.classList.toggle('on', !jointLimits); });
    const out = document.createElement('input'); out.readOnly = true; out.style.cssText = 'width:260px;font:10px monospace;background:#111;color:#bbb;border:1px solid #444;padding:3px';
    btn('JSON 복사', () => { const j = JSON.stringify(target.getPose()); out.value = j; void navigator.clipboard?.writeText(j); out.select(); });
    panel.appendChild(out);
    // **저장 = 바로 적용.** 서버 /api/poses 에 이름으로 넣는다. 다음 접속부터 모든 클라이언트가 이 자세를 쓴다
    const name = document.createElement('input'); name.value = 'corpse'; name.placeholder = '이름'; name.style.cssText = 'width:80px;font:11px monospace;background:#111;color:#eee;border:1px solid #444;padding:3px';
    panel.appendChild(name);
    const status = document.createElement('span'); status.style.color = '#9fd39f';
    btn('저장', async () => {
      const r = await fetch('/api/poses', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name: name.value, pose: target.getPose() }) });
      const j = await r.json().catch(() => ({ ok: false }));
      status.textContent = j.ok ? `저장됨 · ${j.name}` : '저장 실패';
      refreshList();
    });
    const sel = document.createElement('select'); sel.style.cssText = 'font:11px monospace;background:#111;color:#eee;border:1px solid #444';
    const refreshList = async () => {
      const r = await fetch('/api/poses', { cache: 'no-store' }); const all = (await r.json().catch(() => ({}))) as Record<string, Pose>;
      sel.innerHTML = '<option value="">불러오기…</option>' + Object.keys(all).map((k) => `<option value="${k}">${k}</option>`).join('');
      sel.onchange = () => { const p = all[sel.value]; if (p) { target.setPose(p); update(); status.textContent = `불러옴 · ${sel.value}`; } };
    };
    void refreshList();
    panel.appendChild(sel); panel.appendChild(status);
    const tip = document.createElement('span'); tip.textContent = '핀 클릭=기즈모 · 핀 끌기=관절 돌리기'; tip.style.color = '#9aa3b2'; panel.appendChild(tip);
    document.body.appendChild(panel);
  };
  const enable = () => {
    if (on) return true;
    if (!build()) return false;
    on = true;
    if (opts.panel !== false) makePanel();   // 전용 페이지(/poser.html)는 자기 패널이 있다
    document.exitPointerLock?.();
    canvas.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('keydown', onKey, true);
    scene.add(tc.getHelper());
    update();
    return true;
  };
  const disable = () => {
    on = false;
    detach();
    panel?.remove(); panel = null;
    scene.remove(tc.getHelper());
    canvas.removeEventListener('pointerdown', onDown, true);
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', onUp, true);
    window.removeEventListener('keydown', onKey, true);
    scene.remove(group);
    group.clear();
    pins.clear();
    helper = null;
  };
  /** 자동화용 — 핀을 월드 좌표로 끈 것과 같다 */
  const dragTo = (joint: string, x: number, y: number, z: number) => {
    const model = target.model(); const rest = target.rest();
    const pin = pins.get(joint); if (!model || !rest || !pin) return false;
    const parentName = pin.userData.parent as string | null; if (!parentName) return false;
    const j = boneOf(model, joint), parent = boneOf(model, parentName);
    if (!j || !parent || !parent.parent) return false;
    const pw = parent.getWorldPosition(new THREE.Vector3());
    const cur = j.getWorldPosition(new THREE.Vector3()).sub(pw).normalize();
    const want = new THREE.Vector3(x, y, z).sub(pw).normalize();
    const R = new THREE.Quaternion().setFromUnitVectors(cur, want);
    const P = parent.parent.getWorldQuaternion(new THREE.Quaternion());
    const next = P.clone().invert().multiply(R).multiply(P).multiply(parent.quaternion);
    const restB = rest.get(parent.name); if (!restB) return false;
    const rel = restB.q.clone().invert().multiply(next);
    target.setPose({ ...target.getPose(), [parentName]: decompose(parentName, rel) });
    update();
    return true;
  };

  /** 자동화용 — 기즈모를 붙이고 모드를 정한 뒤 뼈를 직접 바꾼 것처럼 objectChange 를 흘린다 */
  const gizmo = (joint: string, mode: 'rotate' | 'translate' | 'scale' = 'rotate') => { tc.setMode(mode); return attach(joint); };
  const gizmoTest = (joint: string, mode: 'rotate' | 'translate' | 'scale', v: number[]) => {
    if (!gizmo(joint, mode)) return false;
    const model = target.model(); const b = model && boneOf(model, joint); if (!b) return false;
    if (mode === 'rotate') b.quaternion.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(v[0], v[1], v[2])));
    else if (mode === 'translate') b.position.add(new THREE.Vector3(v[0], v[1], v[2]));
    else b.scale.set(v[0], v[1], v[2]);
    b.updateMatrixWorld(true);
    onObjectChange();
    return true;
  };
  /** 기즈모를 끄는 동안 카메라 컨트롤을 멈추고 싶을 때 */
  const onDragging = (fn: (dragging: boolean) => void) => tc.addEventListener('dragging-changed', (e) => fn(!!(e as unknown as { value: boolean }).value));
  return { enable, disable, update, dragTo, gizmo, gizmoTest, detach, onDragging, isOn: () => on };
}
